import { motion } from 'framer-motion';
import { 
  MapPin, Phone, Clock, MessageCircle, Star,
  Shirt, Droplets, Timer, Truck, ChevronRight, Mail,
  CheckCircle2
} from 'lucide-react';

const DemoHomeCare = () => {
  const business = {
    name: "HomeCareService",
    tagline: "Premium was- en strijkservice aan huis",
    website: "www.homecareservice.me",
    hero: "https://images.unsplash.com/photo-1545173168-9f1947eebb7f?w=800&q=72&auto=format",
    rating: 4.9,
    reviews: 89,
    address: "Dorpstraat 16, 3390 Tielt-Winge",
    phone: "+32 486 998 995",
    whatsapp: "32486998995",
    email: "info@homecareservice.me",
    hours: "Ma-Za: 08:00-20:00 | Zo: 10:00-18:00",
  };

  const services = [
    { icon: Droplets, name: 'Wassen', desc: 'Professioneel gewassen' },
    { icon: Shirt, name: 'Strijken', desc: 'Perfect gestreken' },
    { icon: Timer, name: 'Express', desc: '24-uurs service' },
    { icon: Truck, name: 'Ophalen', desc: 'Gratis aan huis' },
  ];

  const uspPoints = [
    'Ophalen & bezorgen gratis',
    'Milieuvriendelijk wassen',
    'Track & trace systeem',
    'Klanttevredenheid 99%',
  ];

  const whatsappMessage = encodeURIComponent("Hallo HomeCareService! Ik wil graag gebruik maken van jullie wasservice.");

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white">
      {/* Hero */}
      <div className="relative h-56 overflow-hidden">
        <img 
          src={business.hero} 
          alt={`${business.name} - Professionele wasserij en stomerij service`}
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-sky-50 via-transparent to-transparent" />
        
        {/* Logo */}
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -bottom-10 left-1/2 -translate-x-1/2"
        >
          <div className="w-20 h-20 rounded-2xl border-4 border-white overflow-hidden shadow-xl bg-sky-500 flex items-center justify-center">
            <Shirt className="text-white" size={36} />
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="pt-14 pb-8 px-6 max-w-lg mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-2xl font-bold text-gray-900 mb-1">{business.name}</h1>
          <p className="text-gray-500 text-sm mb-2">{business.tagline}</p>
          <a href={`https://${business.website}`} target="_blank" rel="noopener noreferrer" className="text-sky-600 text-sm font-medium">
            {business.website}
          </a>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  size={14} 
                  className={i < Math.floor(business.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} 
                />
              ))}
            </div>
            <span className="text-gray-900 font-medium">{business.rating}</span>
            <span className="text-gray-400">({business.reviews} reviews)</span>
          </div>
        </motion.div>

        {/* WhatsApp CTA */}
        <motion.a
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          href={`https://wa.me/${business.whatsapp}?text=${whatsappMessage}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-3 bg-[#25D366] text-white py-4 px-6 rounded-2xl font-semibold mb-4 shadow-lg shadow-green-200 hover:shadow-xl transition-shadow"
          data-testid="whatsapp-btn"
        >
          <MessageCircle size={24} fill="white" />
          Plan ophaalafspraak
        </motion.a>

        {/* Email alternative */}
        <motion.a
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          href={`mailto:${business.email}`}
          className="flex items-center justify-center gap-2 text-sky-600 mb-6 text-sm"
        >
          <Mail size={16} />
          Of mail naar {business.email}
        </motion.a>

        {/* USP Points */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-sky-50 rounded-2xl p-4 mb-6"
        >
          <div className="grid grid-cols-2 gap-2">
            {uspPoints.map((point, index) => (
              <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle2 size={16} className="text-sky-500 flex-shrink-0" />
                {point}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 gap-3 mb-6"
        >
          {services.map((service, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <service.icon className="text-sky-500 mb-2" size={24} />
              <h3 className="font-semibold text-gray-900 text-sm">{service.name}</h3>
              <p className="text-gray-500 text-xs">{service.desc}</p>
            </div>
          ))}
        </motion.div>

        {/* Info Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-3 mb-6"
        >
          <div className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm border border-gray-100">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <Clock className="text-sky-600" size={20} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Service uren</p>
              <p className="text-gray-900 font-medium">{business.hours}</p>
            </div>
          </div>
          
          <a 
            href={`https://maps.google.com/?q=${encodeURIComponent(business.address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm border border-gray-100"
          >
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <MapPin className="text-sky-600" size={20} />
            </div>
            <div className="flex-grow">
              <p className="text-gray-400 text-sm">Service gebied</p>
              <p className="text-gray-900 font-medium">Tielt-Winge & Maastricht</p>
            </div>
            <ChevronRight className="text-gray-400" size={20} />
          </a>

          <a 
            href={`tel:${business.phone}`}
            className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm border border-gray-100"
          >
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <Phone className="text-sky-600" size={20} />
            </div>
            <div className="flex-grow">
              <p className="text-gray-400 text-sm">Telefoon</p>
              <p className="text-gray-900 font-medium">{business.phone}</p>
            </div>
            <ChevronRight className="text-gray-400" size={20} />
          </a>
        </motion.div>

        {/* Powered by */}
        <div className="text-center">
          <a href="https://netblitz.eu" target="_blank" rel="noopener noreferrer" className="text-gray-400 text-xs hover:text-sky-600 transition-colors">
            Powered by <span className="font-medium">Netblitz</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default DemoHomeCare;
